import React from 'react';

import { Text } from '@rtkwlf/c3-core-react';
import { Heading, VStack } from '@rtkwlf/fenrir-react';
import type { Blueprint } from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';

import { useGetSpidrFrameworkCategoriesOptions } from '@apis/track-public/query-options';
import { Box, Typography } from '@packages/fenrir/components';

import { useSecurityJourneyTranslations } from '../locales/use-security-journey-translations';

import { FrameworkOverviewAccordion } from './framework-accordion/framework-overview-accordion';
import { SpidrDetailsList } from './spidr-details-list';
import styles from './spidr-details.module.css';
import { TechnicalDomainsList } from './technical-domains-list';

export const SpidrBlueprintDetails: React.FC<{ blueprint: Blueprint }> = ({
  blueprint,
}) => {
  const { t } = useSecurityJourneyTranslations();

  const { data } = useQuery(useGetSpidrFrameworkCategoriesOptions());

  const blueprintTechnicalDomains = new Set([blueprint.primaryTechnicalDomain]);

  if (blueprint.technicalDomains) {
    blueprint.technicalDomains.forEach((technicalDomain) =>
      blueprintTechnicalDomains.add(technicalDomain),
    );
  }

  return (
    <VStack gap="medium" className={styles['spidr-blueprint-details']}>
      <Heading level="5">{t('spidrID.spidrDetails.header')}</Heading>
      <Box>
        <Typography variant="body2" color="textPrimary" marginBottom={1}>
          {t('spidrID.spidrDetails.technicalDomain')}
        </Typography>
        <TechnicalDomainsList
          technicalDomains={Array.from(blueprintTechnicalDomains)}
          color="text.primary"
        />
      </Box>
      <div className={styles['spidr-details-item']}>
        <Text weight="bold" size="2">
          {t('spidrID.spidrDetails.technologies')}
        </Text>
        <SpidrDetailsList value={blueprint.technologies} />
      </div>

      <Box width="100%">
        <FrameworkOverviewAccordion
          framework="nist"
          bpId={blueprint.id}
          elements={blueprint.nist}
          totalFrameworks={blueprint.nist.length}
          totalGroups={
            data
              ? Object.values(data.nistCategories).reduce(
                  (sum, array) => sum + array.length,
                  0,
                )
              : 0
          }
          numberedList
        />
        <FrameworkOverviewAccordion
          framework="nist2"
          bpId={blueprint.id}
          elements={blueprint.nist2}
          totalFrameworks={blueprint.nist2.length}
          totalGroups={
            data
              ? Object.values(data.nist2Categories).reduce(
                  (sum, array) => sum + array.length,
                  0,
                )
              : 0
          }
          numberedList
        />
        <FrameworkOverviewAccordion
          framework="cis"
          bpId={blueprint.id}
          elements={blueprint.cis}
          totalFrameworks={blueprint.cis.length}
          totalGroups={
            data ? Object.keys(data.cisControlsToSafeguards).length : 0
          }
        />
      </Box>

      <div className={styles['spidr-details-item']}>
        <Text weight="bold" size="2">
          {t('spidrID.spidrDetails.subscriptions')}
        </Text>
        <SpidrDetailsList value={blueprint.subscriptions} />
      </div>
    </VStack>
  );
};
